import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchArticles } from "../lib/api";

const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
};

export default function ArticlesSection() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const scrollerRef = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    useEffect(() => {
        let mounted = true;
        fetchArticles()
            .then((data) => {
                if (mounted) { setArticles(data); setLoading(false); }
            })
            .catch((err) => {
                console.error(err);
                if (mounted) { setError("Impossibile caricare gli articoli."); setLoading(false); }
            });
        return () => { mounted = false; };
    }, []);

    const updateScrollState = () => {
        const el = scrollerRef.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > 8);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        updateScrollState();
        el.addEventListener("scroll", updateScrollState, { passive: true });
        window.addEventListener("resize", updateScrollState);
        return () => {
            el.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, [articles]);

    const scrollBy = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const amount = Math.min(el.clientWidth * 0.9, 520);
        el.scrollBy({ left: dir * amount, behavior: "smooth" });
    };

    return (
        <section
            id="articoli"
            data-testid="articles-section"
            className="py-24 lg:py-36 bg-[#FAFAFA] relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">Articoli Tematici</span>
                        </div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            Approfondimenti di
                            <span className="italic text-gold-deep"> diritto del lavoro</span>.
                        </h2>
                        <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
                            Articoli tematici spiegati in modo semplice e accessibile a tutti.
                        </p>
                    </div>

                    {articles.length > 1 && (
                        <div className="flex items-center gap-3" data-testid="articles-nav">
                            <button
                                onClick={() => scrollBy(-1)}
                                disabled={!canLeft}
                                data-testid="articles-prev"
                                aria-label="Articolo precedente"
                                className="w-12 h-12 border border-navy text-navy flex items-center justify-center transition-all duration-300 hover:bg-navy hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-navy"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <button
                                onClick={() => scrollBy(1)}
                                disabled={!canRight}
                                data-testid="articles-next"
                                aria-label="Articolo successivo"
                                className="w-12 h-12 border border-navy text-navy flex items-center justify-center transition-all duration-300 hover:bg-navy hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-navy"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    )}
                </div>

                {loading && (
                    <div className="py-20 text-center text-slate-500 font-mono text-sm" data-testid="articles-loading">
                        Caricamento articoli in corso...
                    </div>
                )}

                {error && (
                    <div className="py-20 text-center text-red-600" data-testid="articles-error">{error}</div>
                )}

                {!loading && !error && articles.length === 0 && (
                    <div className="py-20 text-center text-slate-500 font-serif text-xl" data-testid="articles-empty">
                        I primi articoli saranno pubblicati a breve.
                    </div>
                )}
            </div>

            {!loading && !error && articles.length > 0 && (
                <div className="relative">
                    <div
                        ref={scrollerRef}
                        data-testid="articles-scroller"
                        className="overflow-x-auto overflow-y-hidden scroll-smooth no-scrollbar"
                        style={{ scrollSnapType: "x mandatory" }}
                    >
                        <div className="flex gap-6 px-6 lg:px-10 pb-4 max-w-[calc(100vw)] lg:max-w-none">
                            {/* Padding spacer to align with content max-width */}
                            <div className="hidden xl:block flex-shrink-0" style={{ width: "max(0px, calc((100vw - 1280px) / 2))" }} aria-hidden="true" />
                            {articles.map((article, idx) => (
                                <Link
                                    key={article.id}
                                    to={`/articolo/${article.slug}`}
                                    data-testid={`article-card-${article.slug}`}
                                    className="group flex-shrink-0 w-[88vw] sm:w-[400px] lg:w-[420px] bg-white border border-slate-200 hover:border-gold p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_25px_60px_-30px_rgba(11,19,43,0.35)] flex flex-col relative overflow-hidden"
                                    style={{ scrollSnapAlign: "start" }}
                                >
                                    <div className="absolute top-0 left-0 w-0 h-[2px] bg-gold transition-all duration-500 group-hover:w-full" />

                                    <div className="flex items-center justify-between mb-6">
                                        <span className="inline-block px-3 py-1 border border-navy/20 text-navy text-[10px] uppercase tracking-[0.25em] font-bold group-hover:border-gold group-hover:text-gold-deep transition-colors">
                                            {article.categoria}
                                        </span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                            {String(idx + 1).padStart(2, "0")} / {String(articles.length).padStart(2, "0")}
                                        </span>
                                    </div>

                                    <h3 className="font-serif text-2xl md:text-[26px] text-navy leading-tight mb-5 line-clamp-3 min-h-[6rem]">
                                        {article.titolo}
                                    </h3>

                                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-3 mb-6 flex-1">
                                        {article.sommario}
                                    </p>

                                    <div className="flex items-center justify-between pt-5 border-t border-slate-200">
                                        <div className="flex flex-col gap-1 text-xs">
                                            <span className="font-mono uppercase tracking-[0.18em] text-gold-deep">
                                                {formatDate(article.published_at)}
                                            </span>
                                            <span className="flex items-center gap-1.5 text-slate-500">
                                                <Clock size={11} />
                                                {article.minuti_lettura} min di lettura
                                            </span>
                                        </div>
                                        <div className="w-11 h-11 border border-slate-300 group-hover:border-gold group-hover:bg-gold flex items-center justify-center transition-all duration-300">
                                            <ArrowUpRight size={18} className="text-navy transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                            <div className="flex-shrink-0 w-6 lg:w-10" aria-hidden="true" />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
